import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { getMeeshoDashboard } from "app/redux/slice/meeshoSlice";
import Loader from "@jumbo/components/Loader";
import { MEESHO } from "app/routes/constant";
import { useNavigate } from "react-router-dom";

const MeeshoDashboard = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const meeshoData = useSelector((state) => state?.meesho);
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    // Load dashboard data on component mount
    dispatch(getMeeshoDashboard());
  }, [dispatch]);

  const handleDateRangeChange = (field, value) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilter = () => {
    const params = {};
    if (dateRange.startDate) params.startDate = dateRange.startDate;
    if (dateRange.endDate) params.endDate = dateRange.endDate;
    dispatch(getMeeshoDashboard(params));
  };

  const handleResetFilter = () => {
    setDateRange({ startDate: "", endDate: "" });
    dispatch(getMeeshoDashboard());
  };

  const dashboardData = meeshoData?.dashboard?.data;
  const isLoading = meeshoData?.dashboard?.loading;

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Format number
  const formatNumber = (num) => {
    if (!num) return "0";
    return new Intl.NumberFormat("en-IN").format(num);
  };

  const stats = [
    {
      title: "Total Orders",
      value: formatNumber(dashboardData?.stats?.totalOrders?.value || 0),
      change: dashboardData?.stats?.totalOrders?.growth || "0%",
      icon: "material-symbols:shopping-cart",
      color: "#009688",
    },
    {
      title: "Total RTO",
      value: formatNumber(dashboardData?.stats?.totalRTO?.value || 0),
      change: dashboardData?.stats?.totalRTO?.growth || "0%",
      icon: "material-symbols:assignment-return",
      color: "#9C27B0",
    },
    {
      title: "Revenue",
      value: formatCurrency(dashboardData?.stats?.totalRevenue?.value || 0),
      change: dashboardData?.stats?.totalRevenue?.growth || "0%",
      icon: "material-symbols:currency-rupee",
      color: "#3F51B5",
    },
  ];

  const hitProducts = dashboardData?.topProducts || [];
  const ordersByState = dashboardData?.ordersByState || [];
  const topCities = dashboardData?.topCities || [];

  return (
    <Box>
      {isLoading && <Loader />}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}
      >
        <Typography variant="h3" sx={{ fontWeight: "bold" }}>
          Meesho Analytics Dashboard
        </Typography>
        <Button
          variant="contained"
          size="small"
          sx={{ width: "100px" }}
          startIcon={<Icon icon="ep:back" width="24" height="24" />}
          onClick={() => navigate(MEESHO)}
        >
          Back
        </Button>
      </div>

      {/* Date Range Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                label="Start Date"
                type="date"
                value={dateRange.startDate}
                onChange={(e) =>
                  handleDateRangeChange("startDate", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="End Date"
                type="date"
                value={dateRange.endDate}
                onChange={(e) =>
                  handleDateRangeChange("endDate", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                onClick={handleApplyFilter}
                disabled={isLoading}
                startIcon={
                  isLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Icon icon="material-symbols:filter-alt" />
                  )
                }
                fullWidth
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="outlined"
                onClick={handleResetFilter}
                disabled={isLoading}
                startIcon={<Icon icon="material-symbols:refresh" />}
                fullWidth
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                height: "100%",
                background: `linear-gradient(135deg, ${stat.color}15, ${stat.color}05)`,
              }}
            >
              <CardContent sx={{ textAlign: "center" }}>
                <Icon
                  icon={stat.icon}
                  style={{
                    fontSize: "32px",
                    color: stat.color,
                    marginBottom: "8px",
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", color: stat.color, mb: 1 }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {stat.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Hit Products */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Icon
                  icon="material-symbols:trending-up"
                  style={{ marginRight: "8px", color: "#4caf50" }}
                />
                Top Performing Products
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="center">Orders</TableCell>
                      <TableCell align="center">Revenue</TableCell>
                      <TableCell align="center">Quantity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hitProducts.length > 0 ? (
                      hitProducts.map((product, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                {product.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {formatNumber(product.orders)}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: "bold", color: "#4caf50" }}
                          >
                            {formatCurrency(product.revenue)}
                          </TableCell>
                          <TableCell align="center">
                            {formatNumber(product.quantity)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Orders by State */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Icon
                  icon="material-symbols:location-on"
                  style={{ marginRight: "8px", color: "#2196f3" }}
                />
                Orders by State
              </Typography>
              {ordersByState.length > 0 ? (
                ordersByState.map((state, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {state.state}
                      </Typography>
                      <Typography variant="body2">
                        {formatNumber(state.orders)} orders (
                        {state.percentage?.toFixed(1)}%)
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={state.percentage || 0}
                      sx={{ height: 6, borderRadius: 3, mb: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Revenue: {formatCurrency(state.revenue)}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  No data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Cities */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Icon
                  icon="material-symbols:location-city"
                  style={{ marginRight: "8px", color: "#ff9800" }}
                />
                Top Cities by Orders
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>City</TableCell>
                      <TableCell>State</TableCell>
                      <TableCell align="center">Orders</TableCell>
                      <TableCell align="center">Revenue</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topCities.length > 0 ? (
                      topCities.map((city, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {city.city}
                          </TableCell>
                          <TableCell>{city.state}</TableCell>
                          <TableCell align="center">
                            {formatNumber(city.orders)}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: "bold", color: "#4caf50" }}
                          >
                            {formatCurrency(city.revenue)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MeeshoDashboard;
